import React, { FC, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import RefreshIcon from '@mui/icons-material/Refresh';
import { buttonVariants, buttonWidths } from '@clatter/ui';
import { IconButton, NgButton, SkeletonLoader } from '../../UI';
import { ClassificationOption, SlideRowDisplayLevel } from '../../../interfaces';
import { SlideRow, SLIDE_ROW_SKELETON_HEIGHT, SLIDE_ROW_SKELETON_COUNT } from './SlideRow';
import { SLIDE_ROW_LEVEL_COLOR, STATUS_TO_CLASSIFICATION, CLASSIFICATION_TO_STATUS, StyledStatusBadge } from './SlideLevel';
import { SlideColumn } from './SlideColumn';
import { ClassifyDialog, SlideClassification } from './ClassifyDialog';

export interface ResultingSlide {
  id: number;
  slide_number: number;
  slide_title?: string;
  thumbnail_location: string;
  similarity_level?: SlideRowDisplayLevel;
  similar_slides: { id: number; thumbnail_location: string }[];
}

interface ResultingSlidesProps {
  slides: ResultingSlide[];
  originalSlides: FolderManagerItem[];
  classifications: Record<number, SlideClassification>;
  onClassify: (newSlideId: number, classification: SlideClassification) => void;
  selectedSlideId?: number | null;
  onSlideClick?: (originalSlideId: string, resultSlideId: number) => void;
  hasError?: boolean;
  onRetry?: () => void;
  viewStateKey?: string;
}

// lets the Classify button's click stop propagation to the row's onClick without disturbing
// StyledRightSlot's flex layout (display: contents removes this wrapper's own box)
const StyledClickGuard = styled.div`
  display: contents;
`;

const StyledLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.palette.textDimmed};
`;

const StyledLegendDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  margin-right: 4px;
`;

const StyledRetryState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.small};
  min-height: 300px;
  width: 100%;
`;

export const ResultingSlides: FC<ResultingSlidesProps> = ({
  slides,
  originalSlides,
  classifications,
  onClassify,
  selectedSlideId,
  onSlideClick,
  hasError,
  onRetry,
  viewStateKey,
}) => {
  const theme = useTheme();
  const [classifyingSlide, setClassifyingSlide] = useState<ResultingSlide | null>(null);

  const getEffectiveLevel = (slide: ResultingSlide): SlideRowDisplayLevel | undefined => {
    const override = classifications[slide.id];
    return override ? CLASSIFICATION_TO_STATUS[override.option] : slide.similarity_level;
  };

  const getMatchedOriginalSlideId = (slide: ResultingSlide, level?: SlideRowDisplayLevel): string | undefined => {
    if (level !== SlideRowDisplayLevel.Similar && level !== SlideRowDisplayLevel.Duplicate) return undefined;

    const override = classifications[slide.id];
    if (override?.replacesSlideId) return override.replacesSlideId;

    return slide.similar_slides[0] ? String(slide.similar_slides[0].id) : undefined;
  };

  const classifyingOverride = classifyingSlide ? classifications[classifyingSlide.id] : undefined;

  // original slides already targeted by other resulting slides — excluded from the classifying
  // slide's own dropdown so two new slides can't both claim the same original slide
  const excludedOriginalSlideIds = new Set(
    slides
      .filter((slide) => slide.id !== classifyingSlide?.id)
      .map((slide) => getMatchedOriginalSlideId(slide, getEffectiveLevel(slide)))
      .filter((originalSlideId): originalSlideId is string => !!originalSlideId),
  );

  return (
    <SlideColumn
      title="Resulting Slides"
      subtitle={
        <StyledLegend>
          <span>
            <StyledLegendDot $color={SLIDE_ROW_LEVEL_COLOR[SlideRowDisplayLevel.New]} />
            New slide
          </span>
          <span>
            <StyledLegendDot $color={SLIDE_ROW_LEVEL_COLOR[SlideRowDisplayLevel.Similar]} />
            New version
          </span>
          <span>
            <StyledLegendDot $color={SLIDE_ROW_LEVEL_COLOR[SlideRowDisplayLevel.Duplicate]} />
            Duplicate
          </span>
        </StyledLegend>
      }
    >
      {slides.length > 0
        ? slides.map((slide) => {
            const level = getEffectiveLevel(slide);
            const matchedOriginalSlideId = getMatchedOriginalSlideId(slide, level);

            return (
              <SlideRow
                key={slide.id}
                level={level}
                slideNumber={slide.slide_number}
                thumbnailUrl={slide.thumbnail_location}
                viewStateKey={viewStateKey}
                title={slide.slide_title}
                isHighlighted={selectedSlideId === slide.id}
                onClick={
                  matchedOriginalSlideId ? () => onSlideClick?.(matchedOriginalSlideId, slide.id) : undefined
                }
                rightSlot={
                  <StyledClickGuard onClick={(event) => event.stopPropagation()}>
                    {level && <StyledStatusBadge $level={level} />}
                    <NgButton
                      variant={buttonVariants.primary}
                      width={buttonWidths.xs}
                      onClick={() => setClassifyingSlide(slide)}
                    >
                      Classify ›
                    </NgButton>
                  </StyledClickGuard>
                }
              />
            );
          })
        : hasError
        ? (
          <StyledRetryState>
            <IconButton onClick={onRetry} size="1.5rem">
              <RefreshIcon fontSize="inherit" />
            </IconButton>
            Retry
          </StyledRetryState>
        )
        : Array.from({ length: SLIDE_ROW_SKELETON_COUNT }).map((_, index) => (
            <SkeletonLoader
              key={index}
              count={1}
              height={SLIDE_ROW_SKELETON_HEIGHT}
              width="100%"
              borderRadius={theme.radius.card}
              single
            />
          ))}

      <ClassifyDialog
        key={classifyingSlide?.id}
        open={!!classifyingSlide}
        slideTitle={classifyingSlide?.slide_title}
        thumbnailUrl={classifyingSlide?.thumbnail_location}
        originalSlides={originalSlides}
        excludedOriginalSlideIds={excludedOriginalSlideIds}
        viewStateKey={viewStateKey}
        initialOption={
          classifyingOverride?.option ??
          (classifyingSlide?.similarity_level
            ? STATUS_TO_CLASSIFICATION[classifyingSlide.similarity_level]
            : ClassificationOption.NewSlide)
        }
        initialReplacesSlideId={classifyingOverride?.replacesSlideId}
        onClose={() => setClassifyingSlide(null)}
        onSave={(option, replacesSlideId) => {
          if (classifyingSlide) onClassify(classifyingSlide.id, { option, replacesSlideId });
        }}
      />
    </SlideColumn>
  );
};
